import { FC, ReactElement, ReactNode } from 'react';

import AddOnIcon from '../../assets/icons/miniBasket/AddOn.svg';
import AttendeeTypeIcon from '../../assets/icons/miniBasket/AttendeeType.svg';
import CalenderIcon from '../../assets/icons/miniBasket/Calender.svg';

import DateRange from './DateRange';
import QuantityControl from './QuantityControl';

/**
 * Shared LineItem component for MiniBasket, Order Summary, and Add-ons.
 */

export interface LineItemData {
    id: string;
    title?: string | Record<string, string>;
    name?: string | Record<string, string>;
    subtitle?: string | ReactNode;
    description?: string;
    passType?: string;
    delegateRate?: string;
    quantity?: number;
    currencyCode?: string;
    startDate?: string | Date | null;
    endDate?: string | Date | null;
    price?: {
        value?: { centAmount: number };
        centAmount?: number;
    } | number;
    totalPrice?: {
        centAmount: number;
        currencyCode?: string;
    };
    addOns?: any[];
    custom?: {
        fields?: {
            addOns?: string | any;
            delegateRate?: string;
        };
    };
}

export interface LineItemProps {
    item: LineItemData;
    variant?: 'cart' | 'summary' | 'selectable';
    currencyCode?: string;
    locale?: string;
    onIncrement?: (id: string) => void;
    onDecrement?: (id: string) => void;
    onDelete?: (id: string) => void;
    selected?: boolean;
    onToggle?: (id: string, checked: boolean) => void;
    isUpdating?: boolean;
}


// ── Component ──────────────────────────────────────────────────────────────

const LineItem: FC<LineItemProps> = ({
    item,
    variant = 'cart',
    currencyCode = 'USD',
    locale = 'en',
    onIncrement,
    onDecrement,
    onDelete: _onDelete,
    selected = false,
    onToggle,
    isUpdating = false,
}): ReactElement => {
    // Resolve display name
    let title: any = item.title || item.name;
    if (typeof title === 'object' && title !== null) {
        title = title['en'] || Object.values(title)[0] || '';
    }

    // Resolve delegate / attendee type
    const attendeeType =
        item.delegateRate ||
        (item.custom?.fields?.delegateRate as string | undefined) ||
        '';

    // Resolve add-ons list
    let addOnsList: any[] = item.addOns || [];
    if (addOnsList.length === 0 && item.custom?.fields?.addOns) {
        try {
            const raw = item.custom.fields.addOns;
            addOnsList =
                typeof raw === 'string' && (raw.startsWith('[') || raw.startsWith('"'))
                    ? JSON.parse(raw)
                    : [raw];
            if (!Array.isArray(addOnsList)) addOnsList = [addOnsList];
        } catch (err) {
            console.error('Failed to parse addOns:', err);
            addOnsList = [item.custom?.fields?.addOns];
        }
    }

    // Price
    const calculateLineTotal = (): number => {
        if (item.totalPrice?.centAmount !== undefined) return item.totalPrice.centAmount / 100;
        let unit = 0;
        if ((item.price as any)?.value?.centAmount !== undefined)
            unit = (item.price as any).value.centAmount / 100;
        else if ((item.price as any)?.centAmount !== undefined)
            unit = (item.price as any).centAmount / 100;
        else if (typeof item.price === 'number') unit = item.price;
        return unit * (item.quantity || 1);
    };

    const priceValue = calculateLineTotal();
    const formattedPrice = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: item.currencyCode || item.totalPrice?.currencyCode || currencyCode,
    }).format(priceValue);

    // ── Selectable variant (add-on checkbox row) ─────────────────────────
    if (variant === 'selectable') {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (onToggle) onToggle(item.id, e.target.checked);
        };
        return (
            <label className="flex gap-3 py-3 border-b border-gray-200 last:border-b-0 cursor-pointer transition-all duration-150 hover:bg-gray-50">
                <div className="flex-shrink-0 pt-1">
                    <input
                        type="checkbox"
                        checked={selected}
                        onChange={handleChange}
                        className="rounded border-gray-300 text-black focus:ring-0 focus:outline-none accent-black"
                        style={{ width: 16, height: 16, borderColor: '#D0D5DD' }}
                    />
                </div>
                <div className="flex-1 flex justify-between items-start">
                    <div className="flex-1">
                        <div className="font-bold text-gray-900 text-sm">{title}</div>
                        {item.description && (
                            <div className="text-xs text-gray-600 mt-0.5">{item.description}</div>
                        )}
                    </div>
                    <div className="font-semibold text-gray-900 text-sm ml-4">{formattedPrice}</div>
                </div>
            </label>
        );
    }

    // ── Cart / Summary variant ────────────────────────────────────────────
    const qty = item.quantity || 1;
    const addOnCount = addOnsList.length;

    // shared sub-text colour: #002B66 @ 70%
    const subTextStyle = { color: 'rgba(0,43,102,0.7)', fontWeight: 400, fontSize: 12, lineHeight: '18px' };

    return (
        <div>
            {/* Pass name */}
            <p
                className="font-semibold text-[14px] leading-[20px] mb-[5px]"
                style={{ color: '#002B66' }}
            >
                {title}
            </p>

            {/* Meta row: Calendar | Date · AttendeeType | Attendee · AddOn | X Add on[s] */}
            <div className="flex items-center flex-wrap gap-x-[8px] gap-y-[2px] mb-[5px]">
                {/* Date */}
                <DateRange
                    startDate={item.startDate}
                    endDate={item.endDate}
                    locale={locale}
                    format="miniBasket"
                    className="flex items-center gap-[2px]"
                    icon={<img src={CalenderIcon} alt="Date" style={{ width: 13, height: 15 }} />}
                    textStyle={{ ...subTextStyle, display: 'inline-block', width: 96, height: 18 }}
                    textClassName="whitespace-nowrap"
                />

                {/* Attendee Type */}
                {attendeeType && (
                    <span className="flex items-center gap-[2px]">
                        <img src={AttendeeTypeIcon} alt="Attendee type" style={{ width: 13, height: 15 }} />
                        <span style={subTextStyle} className="whitespace-nowrap">
                            {attendeeType}
                        </span>
                    </span>
                )}

                {/* Add ons count */}
                {addOnCount > 0 && (
                    <span className="flex items-center gap-[2px]">
                        <img src={AddOnIcon} alt="Add ons" style={{ width: 13, height: 15 }} />
                        <span
                            style={{ ...subTextStyle, display: 'inline-block', width: 56, height: 18 }}
                            className="whitespace-nowrap"
                        >
                            {addOnCount} {addOnCount === 1 ? 'Add on' : 'Add ons'}
                        </span>
                    </span>
                )}
            </div>

            {/* Controls row: [delete/minus] [qty] [plus]  →  price */}
            <div className="flex items-center justify-between">
                {variant === 'cart' ? (
                    <QuantityControl
                        quantity={qty}
                        onIncrease={() => onIncrement && onIncrement(item.id)}
                        onDecrease={() => qty === 1 ? (_onDelete && _onDelete(item.id)) : (onDecrement && onDecrement(item.id))}
                        disabled={isUpdating}
                        variant="miniBasket"
                    />
                ) : (
                    <div style={{ color: 'rgba(0,43,102,0.7)', fontSize: 12 }}>Qty: {qty}</div>
                )}

                {/* Price */}
                <span
                    className="text-right ml-[16px]"
                    style={{ color: '#002B66', fontWeight: 400, fontSize: 14, lineHeight: '24px', width: 186, height: 24, display: 'inline-block' }}
                >
                    {formattedPrice}
                </span>
            </div>
        </div>
    );
};

export default LineItem;
